import React from 'react';
import { GatsbyImage, getSrc } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS } from '@contentful/rich-text-types';
import { contentfulRenderOptions } from './embeddedEntries/richTextRenderer/renderContentfulBody';
import PageTemplate from './Informational';
import SEO from './SEO';
import Hero from '../components/Hero';
import CloudinaryMedia from '../components/CloudinaryMedia';
import Video from '../components/Video';
import BlockSection from './embeddedEntries/BlockSection';
import SideBySideSection from './embeddedEntries/SideBySideSection';
import XUpCardsSection from './embeddedEntries/XUpCardsSection';
import CategorySection from './embeddedEntries/CategorySection';
import TilesSection from './embeddedEntries/TilesSection';
import FullWidthImageSection from './embeddedEntries/FullWidthImageSection';
import getThemeClass from '../utils/getThemeClass';
import makeContentfulSectionIdAttr from '../utils/makeContentfulSectionIdAttr';
import styles from './contentfulPage.module.css';

/**
 * @param {FF.ContentfulHeroImage} heroImage
 */
function isHeroVideo(heroImage) {
  return Boolean(
    (heroImage.cloudinaryMedia || [])[0]?.resource_type === 'video' ||
      heroImage.backgroundVideo,
  );
}

/**
 * @param {FF.ContentfulHeroImage} heroImage
 */
function getHeroMedia(heroImage) {
  if (!heroImage) {
    return null;
  }

  const { backgroundImage, backgroundVideo, cloudinaryMedia, withScrim } =
    heroImage;

  if (cloudinaryMedia) {
    return (
      <CloudinaryMedia
        {...cloudinaryMedia[0]}
        fullScreenVideo
        lazyload={false}
        breakpoints={[480, 1000, 2400]}
      />
    );
  }
  if (backgroundVideo) {
    return (
      <Video
        autoPlay
        fullScreen
        description={backgroundVideo.description}
        url={backgroundVideo.url}
      />
    );
  }
  if (backgroundImage) {
    return (
      <GatsbyImage
        alt={backgroundImage.description}
        image={backgroundImage.gatsbyImageData}
        loading="eager"
      />
    );
  }
  return null;
}

/** @param {(FF.ContentfulBlockSection|FF.ContentfulSideBySideSection|FF.ContentfulXUpCardsSection|FF.ContentfulTilesSection|FF.ContentfulCategorySection|FF.ContentfulFullWidthImageSection)} blockSection  */
function getBlockSectionClass(blockSection) {
  let className;
  if (
    ['ContentfulTilesSection', 'ContentfulFullWidthImageSection'].includes(
      blockSection.__typename,
    )
  ) {
    className = styles.blockSectionFullWidth;
  } else {
    className = styles.blockSection;
  }

  if (blockSection.__typename !== 'ContentfulFullWidthImageSection') {
    const themeClass = getThemeClass(blockSection.backgroundColor);
    if (themeClass) {
      className += ` ${themeClass}`;
    }
  }

  return className;
}

/** @param {string} str */
function kebabToCapitalCase(str) {
  return str
    .split('-')
    .map(word => word.slice(0, 1).toUpperCase() + word.slice(1))
    .join('');
}

/**
 * @param {FF.ContentfulBlockSection} blockSection
 */
function renderEmbeddedSection(blockSection) {
  const renderOptionsWithSections = {
    renderNode: {
      ...contentfulRenderOptions.renderNode,
      /** @type {import('@contentful/rich-text-react-renderer').NodeRenderer} */
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        if (node.data.target?.__typename === 'ContentfulSideBySideSection') {
          return (
            <section className={styles.embeddedBlockSection}>
              <SideBySideSection {...node.data.target} />
            </section>
          );
        }
        if (node.data.target?.__typename === 'ContentfulXUpCardsSection') {
          return (
            <section className={styles.embeddedBlockSection}>
              <XUpCardsSection {...node.data.target} />
            </section>
          );
        }
        if (node.data.target?.__typename === 'ContentfulTilesSection') {
          return (
            <section className={styles.embeddedTileSection}>
              <TilesSection {...node.data.target} />
            </section>
          );
        }
        if (node.data.target?.__typename === 'ContentfulCategorySection') {
          return (
            <section className={styles.embeddedBlockSection}>
              <CategorySection
                {...node.data.target}
                categoryNavBGColor={
                  node.data.target.backgroundColor ||
                  blockSection.backgroundColor ||
                  'White (#FFFFFF)'
                }
                isEmbedded
              />
            </section>
          );
        }
        return contentfulRenderOptions.renderNode[BLOCKS.EMBEDDED_ENTRY](
          node,
          children,
        );
      },
    },
  };

  return renderRichText(blockSection.body, renderOptionsWithSections);
}

/**
 * @param {FF.ContentfulPageTemplate} props
 */
export default function ContentfulPage({ location, pageContext }) {
  return (
    <PageTemplate
      mainContentClass="contentfulPage"
      path={location.pathname}
      hero={
        pageContext.data.heroImage && (
          <Hero
            contentfulId={pageContext.data.heroImage.contentful_id}
            media={getHeroMedia(pageContext.data.heroImage)}
            isVideo={isHeroVideo(pageContext.data.heroImage)}
            heroText={{
              backgroundColor:
                pageContext.data.heroImage.underImageBackgroundColor,
              textAlignment: pageContext.data.heroImage.textAlignment,
              body: pageContext.data.heroImage.body,
            }}
            heroType={
              pageContext.data.heroImage.useSignForTextOverlay
                ? 'sign'
                : 'overlay'
            }
            cutouts={pageContext.data.heroImage.cutouts}
            withScrim={pageContext.data.heroImage.withScrim}
          />
        )
      }
    >
      <div
        className={`${styles.content} ${
          styles[
            `contentfulPage${kebabToCapitalCase(pageContext.data.slug)}`
          ] || ''
        }`}
      >
        {pageContext.data.blockSections.map(blockSection => (
          <section
            className={getBlockSectionClass(blockSection)}
            key={blockSection.contentful_id}
            id={makeContentfulSectionIdAttr(blockSection.contentful_id)}
          >
            {blockSection.__typename === 'ContentfulBlockSection' && (
              <BlockSection {...blockSection} className={styles.pageWidth}>
                {renderEmbeddedSection(blockSection)}
              </BlockSection>
            )}

            {blockSection.__typename === 'ContentfulSideBySideSection' && (
              <SideBySideSection
                {...blockSection}
                className={styles.pageWidth}
              />
            )}
            {blockSection.__typename === 'ContentfulXUpCardsSection' && (
              <XUpCardsSection {...blockSection} className={styles.pageWidth} />
            )}
            {blockSection.__typename === 'ContentfulTilesSection' && (
              <TilesSection {...blockSection} />
            )}
            {blockSection.__typename === 'ContentfulCategorySection' && (
              <CategorySection {...blockSection} />
            )}
            {blockSection.__typename === 'ContentfulFullWidthImageSection' && (
              <FullWidthImageSection {...blockSection} />
            )}
          </section>
        ))}
      </div>
    </PageTemplate>
  );
}

/**
 * @param {object} props
 * @param {{ [key: string]: any }} props.pageContext
 */
export function Head({ pageContext }) {
  return (
    <SEO
      description={pageContext.data.description?.description}
      pageTitle={pageContext.data.title}
      image={getSrc(
        pageContext.data.heroImage?.backgroundImage?.gatsbyImageData,
      )}
    />
  );
}
